import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, hoy } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { descargar, puedeCompartirArchivos } from '../lib/archivos.js';
import {
  aCsv,
  tablaAbonos,
  tablaClientes,
  tablaCompras,
  tablaDiario,
  tablaGastos,
  tablaInventario,
  tablaPrecios,
  tablaVentas,
  tablaVentasDetalle,
  type Rango,
  type Tabla,
} from '../lib/exportar.js';
import { asientosDelRango, comoRango, periodos } from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Chip, Ico, type NombreIcono, type Tinte } from '../ui/Iconos.js';
import { traducir, useT, type Clave } from '../i18n/index.js';

const nombres = async (tabla: 'clientes' | 'proveedores') =>
  new Map((await db[tabla].toArray()).map((x) => [x.id, x.nombre]));

/** The period's sales off the date index: a month out of years of them, without reading the years. */
const ventasDe = (r: Rango) =>
  r.desde || r.hasta
    ? db.ventas.where('fecha').between(r.desde ?? '', r.hasta ?? '\uffff', true, true).toArray()
    : db.ventas.toArray();

interface Conjunto {
  /** Also the file's name: Spanish, the shop's language, whatever the screen's. */
  clave: string;
  titulo: Clave;
  descripcion: Clave;
  icono: NombreIcono;
  tinte: Tinte;
  /** False: how things stand now, whatever the period. */
  conPeriodo: boolean;
  armar: (rango: Rango) => Promise<Tabla>;
}

const CONJUNTOS: readonly Conjunto[] = [
  {
    clave: 'ventas',
    titulo: 'exportar.ventas',
    descripcion: 'exportar.ventasDesc',
    icono: 'ventas',
    tinte: 'marca',
    conPeriodo: true,
    armar: async (r) => tablaVentas(await ventasDe(r), await nombres('clientes'), r, traducir),
  },
  {
    clave: 'ventas-por-producto',
    titulo: 'exportar.detalle',
    descripcion: 'exportar.detalleDesc',
    icono: 'productos',
    tinte: 'marca',
    conPeriodo: true,
    armar: async (r) => tablaVentasDetalle(await ventasDe(r), await nombres('clientes'), r, traducir),
  },
  {
    clave: 'inventario',
    titulo: 'exportar.inventario',
    descripcion: 'exportar.inventarioDesc',
    icono: 'inventario',
    tinte: 'cielo',
    conPeriodo: false,
    armar: async () => tablaInventario(await db.productos.toArray(), await db.existencias.toArray(), traducir),
  },
  {
    clave: 'precios',
    titulo: 'exportar.precios',
    descripcion: 'exportar.preciosDesc',
    icono: 'precios',
    tinte: 'cielo',
    conPeriodo: false,
    armar: async () =>
      tablaPrecios(
        await db.productos.toArray(),
        await db.unidades.toArray(),
        await db.precios.toArray(),
        await db.listas.toArray(),
        traducir,
      ),
  },
  {
    clave: 'clientes',
    titulo: 'exportar.clientes',
    descripcion: 'exportar.clientesDesc',
    icono: 'clientes',
    tinte: 'violeta',
    conPeriodo: false,
    armar: async () => tablaClientes(await db.clientes.toArray(), await db.listas.toArray(), traducir),
  },
  {
    clave: 'abonos',
    titulo: 'exportar.abonos',
    descripcion: 'exportar.abonosDesc',
    icono: 'fiado',
    tinte: 'violeta',
    conPeriodo: true,
    armar: async (r) => tablaAbonos(await db.abonos.toArray(), await nombres('clientes'), r, traducir),
  },
  {
    clave: 'compras',
    titulo: 'exportar.compras',
    descripcion: 'exportar.comprasDesc',
    icono: 'compras',
    tinte: 'cielo',
    conPeriodo: true,
    armar: async (r) => tablaCompras(await db.compras.toArray(), await nombres('proveedores'), r, traducir),
  },
  {
    clave: 'gastos',
    titulo: 'exportar.gastos',
    descripcion: 'exportar.gastosDesc',
    icono: 'gastos',
    tinte: 'esmeralda',
    conPeriodo: true,
    armar: async (r) => tablaGastos(await db.gastos.toArray(), await nombres('proveedores'), r, traducir),
  },
  {
    clave: 'libro-diario',
    titulo: 'exportar.diario',
    descripcion: 'exportar.diarioDesc',
    icono: 'ganancia',
    tinte: 'esmeralda',
    conPeriodo: true,
    armar: async (r) => tablaDiario(await asientosDelRango(r), r, traducir, (await readSettings()).cuentasBanco),
  },
];

/**
 * Descargar datos: the records as spreadsheets, for the accountant, for Excel, or to keep.
 *
 * On a phone the files go wherever the phone can send them — WhatsApp to the accountant, the
 * mail, the drive — and on a computer they download. Each file is built when it is asked for,
 * from this device's copy, so nothing here needs a signal.
 */
export function Exportar() {
  const t = useT();
  const puede = usePuede('ver_reportes');
  const opciones = useMemo(() => periodos(), []);
  // This month: what an accountant asks for. By its name: «Ayer» came before it on 2026-10-14.
  const [indice, setIndice] = useState(() => Math.max(0, opciones.findIndex((p) => p.etiqueta === 'periodo.esteMes')));
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const compartir = useMemo(() => puedeCompartirArchivos(), []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);

  if (!puede) {
    return (
      <Pagina titulo={t('exportar.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  const periodo = opciones[indice]!;
  const rango = comoRango(periodo);
  const dia = hoy();
  const nombreArchivo = (c: Conjunto) =>
    c.conPeriodo
      ? `${c.clave}_${periodo.desde || 'inicio'}_${periodo.hasta || dia}.csv`
      : `${c.clave}_${dia}.csv`;

  async function archivo(c: Conjunto): Promise<File> {
    return new File([aCsv(await c.armar(rango))], nombreArchivo(c), { type: 'text/csv' });
  }

  async function hacer(clave: string, accion: () => Promise<void>) {
    setOcupado(clave);
    setError(null);
    try {
      await accion();
    } catch (e) {
      // Closing the share sheet is a choice, not a failure.
      if (!(e instanceof DOMException && e.name === 'AbortError')) setError(t('exportar.error'));
    } finally {
      setOcupado(null);
    }
  }

  const titulo = t('exportar.compartirTitulo', { negocio: config.negocioNombre });

  return (
    <Pagina titulo={t('exportar.titulo')} subtitulo={t(periodo.etiqueta)}>
      <div className="mb-3 flex flex-wrap gap-2">
        {opciones.map((p, i) => (
          <button
            key={p.etiqueta}
            type="button"
            onClick={() => setIndice(i)}
            data-sin-icono="pestana"
            className={`rounded-lg px-3 py-1.5 text-sm transition ${
              i === indice ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
            }`}
          >
            {t(p.etiqueta)}
          </button>
        ))}
      </div>
      <p className="mb-4 text-xs text-slate-400">{t('exportar.explicacion')}</p>

      <div className="mb-4 flex flex-wrap gap-2">
        {compartir && (
          <button
            type="button"
            className="btn-primario flex-1 basis-40"
            disabled={ocupado !== null}
            onClick={() =>
              void hacer('todo', async () => {
                const archivos = await Promise.all(CONJUNTOS.map(archivo));
                await navigator.share({ files: archivos, title: titulo });
              })
            }
          >
            <Ico icono="compartir" />
            {t('exportar.compartirTodo')}
          </button>
        )}
        <button
          type="button"
          className={`${compartir ? 'btn-secundario' : 'btn-primario'} flex-1 basis-40`}
          disabled={ocupado !== null}
          onClick={() =>
            void hacer('todo', async () => {
              for (const c of CONJUNTOS) {
                descargar(await archivo(c));
                // One at a time: a browser asked for nine files at once saves one.
                await new Promise((r) => setTimeout(r, 400));
              }
            })
          }
        >
          <Ico icono="descargar" />
          {t('exportar.descargarTodo')}
        </button>
      </div>

      {error && <p className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>}

      <ul className="escalonar flex flex-col gap-2">
        {CONJUNTOS.map((c) => (
          <li key={c.clave} className="tarjeta flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <Chip icono={c.icono} tinte={c.tinte} />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{t(c.titulo)}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t(c.descripcion)}
                  {!c.conPeriodo && ` ${t('exportar.comoEstaHoy')}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-3 text-sm"
                disabled={ocupado !== null}
                onClick={() => void hacer(c.clave, async () => descargar(await archivo(c)))}
              >
                <Ico icono="descargar" className="size-4" />
                {ocupado === c.clave ? t('exportar.preparando') : t('exportar.descargar')}
              </button>
              {compartir && (
                <button
                  type="button"
                  className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-3 text-sm"
                  disabled={ocupado !== null}
                  onClick={() =>
                    void hacer(c.clave, async () => {
                      await navigator.share({ files: [await archivo(c)], title: t(c.titulo) });
                    })
                  }
                >
                  <Ico icono="compartir" className="size-4" />
                  {t('exportar.compartir')}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Pagina>
  );
}
